/**
 * Turns the renderer-agnostic fragment bodies of core/definitions.ts into
 * complete GLSL ES 3.00 fragment shaders. The wrapped string is also the
 * program cache key.
 */

/**
 * Uniform names the PixiJS mesh pipeline sets on every program it draws. A
 * filter that declares one of these itself would have its value overwritten,
 * so the wrapper renames them inside the fragment with a #define.
 */
const PIXI_UNIFORMS = [
  'uColor',
  'uRound',
  'uTransformMatrix',
  'uProjectionMatrix',
  'uWorldTransformMatrix',
  'uWorldColorAlpha',
  'uResolution',
] as const;
const PIXI_UNIFORM_SET: ReadonlySet<string> = new Set(PIXI_UNIFORMS);

/** Name of a declared uniform in the linked program. */
export function glUniformName(name: string): string {
  return PIXI_UNIFORM_SET.has(name) ? `uStudio_${name}` : name;
}

const RENAMES = PIXI_UNIFORMS.map((name) => `#define ${name} uStudio_${name}`).join('\n');

const HEADER = `#version 300 es
precision highp float;
precision highp int;
${RENAMES}
in vec2 vUv;
out vec4 fragColor;
uniform float uTime;
`;

const FILTER_HEADER = `${HEADER}uniform sampler2D uSource;
uniform vec2 uSourceSize;
#line 1
`;

const TRANSITION_HEADER = `${HEADER}uniform sampler2D uFrom;
uniform sampler2D uTo;
uniform float uProgress;
uniform vec2 uResolution;
#line 1
`;

export function wrapFilterFragment(fragment: string): string {
  return FILTER_HEADER + fragment;
}

export function wrapTransitionFragment(fragment: string): string {
  return TRANSITION_HEADER + fragment;
}

export const FILTER_BUILTINS: ReadonlySet<string> = new Set(['uSource', 'uSourceSize', 'uTime']);
export const TRANSITION_BUILTINS: ReadonlySet<string> = new Set(['uFrom', 'uTo', 'uProgress', 'uResolution', 'uTime']);

export interface UniformDecl {
  name: string;
  /** GLSL type keyword: float, vec3, sampler2D, ... */
  type: string;
  /** Array length, 1 for non-arrays. */
  size: number;
}

const UNIFORM_RE = /\buniform\s+(?:(?:lowp|mediump|highp)\s+)?(\w+)\s+(\w+)\s*(?:\[\s*(\d+)\s*\])?\s*;/g;

/** Uniform declarations in a fragment body (one declarator per statement). */
export function parseUniforms(fragment: string): UniformDecl[] {
  const code = fragment.replace(/\/\*[\s\S]*?\*\//g, ' ').replace(/\/\/[^\n]*/g, ' ');
  const result: UniformDecl[] = [];
  const seen = new Set<string>();
  for (const match of code.matchAll(UNIFORM_RE)) {
    const name = match[2]!;
    if (seen.has(name)) continue;
    seen.add(name);
    result.push({ name, type: match[1]!, size: match[3] ? Math.max(1, Number(match[3])) : 1 });
  }
  return result;
}

/** '#rgb', '#rrggbb' or '#rrggbbaa' to 0..1 components; invalid input yields opaque black. */
export function parseColor(value: unknown, out: number[] = [0, 0, 0, 1]): number[] {
  out[0] = 0;
  out[1] = 0;
  out[2] = 0;
  out[3] = 1;
  if (typeof value !== 'string') return out;
  let hex = value.trim().replace(/^#/, '');
  if (hex.length === 3 || hex.length === 4) hex = [...hex].map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(hex)) return out;
  for (let i = 0; i < hex.length / 2; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16) / 255;
  return out;
}

export function num(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** Largest rectangle of the source aspect that fits the destination, centred. Writes x, y, w, h into `out`. */
export function fitRect(srcW: number, srcH: number, dstW: number, dstH: number, out: number[]): number[] {
  if (srcW <= 0 || srcH <= 0 || dstW <= 0 || dstH <= 0) {
    out[0] = out[1] = out[2] = out[3] = 0;
    return out;
  }
  const scale = Math.min(dstW / srcW, dstH / srcH);
  const w = Math.max(1, Math.round(srcW * scale));
  const h = Math.max(1, Math.round(srcH * scale));
  out[0] = Math.floor((dstW - w) / 2);
  out[1] = Math.floor((dstH - h) / 2);
  out[2] = w;
  out[3] = h;
  return out;
}
